import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECURRENCE_CYCLES,
  RECURRENCE_CYCLE_MAP,
  RECURRING_STATUSES,
  RECURRING_STATUS_MAP,
  type RecurrenceCycle,
  type RecurringStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface RecurringRow {
  id: string;
  title: string;
  status: RecurringStatus;
  cycle: RecurrenceCycle;
  startDate: string;
  endDate: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  issuedCount: number;
  periodTotal: number;
  autoIssue: boolean;
  account: { id: string; name: string };
}

/**
 * أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في recurring.service.ts.
 *
 * قيمة الدورة وعدد ما صدر محسوبان بعد القراءة لا حقلان في القاعدة، فلا
 * يُفلتر عليهما في الخادم.
 */
const RECURRING_FILTERS: FilterDef[] = [
  { key: 'title', label: 'الاشتراك', kind: 'text', sortable: true },
  { key: 'account', label: 'العميل', kind: 'text' },
  {
    key: 'cycle',
    label: 'الدورة',
    kind: 'enum',
    options: RECURRENCE_CYCLES.map((c) => ({ value: c, label: RECURRENCE_CYCLE_MAP[c].label })),
  },
  { key: 'nextRunAt', label: 'الإصدار التالي', kind: 'date', sortable: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: RECURRING_STATUSES.map((s) => ({ value: s, label: RECURRING_STATUS_MAP[s].label })),
  },
];

interface RecurringPage {
  items: RecurringRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * الاشتراكات الدورية.
 *
 * أكثر عمل متكرر في حياة شركة الخدمات: إصدار نفس الفواتير في أول كل شهر.
 * هذه الشاشة تجعله يقع بلا أحد.
 */
export function RecurringList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);

  const canManage = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['recurring', query],
    queryFn: () => api<RecurringPage>(`/finance/recurring${qs(query)}`),
  });

  const runDue = useMutation({
    mutationFn: () =>
      api<{ issued: number; failed: number }>('/finance/recurring/run-due', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries(),
  });

  const rows = list.data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const due = rows.filter((r) => r.status === 'ACTIVE' && r.nextRunAt <= today);
  const monthly = rows
    .filter((r) => r.status === 'ACTIVE')
    .reduce((s, r) => s + r.periodTotal / RECURRENCE_CYCLE_MAP[r.cycle].months, 0);

  return (
    <div className="page">
      <PageHead
        title="الاشتراكات الدورية"
        subtitle={list.data ? `${list.data.total} اشتراكًا` : undefined}
        actions={
          canManage && (
            <>
              {due.length > 0 && (
                <button
                  className="btn"
                  onClick={() => runDue.mutate()}
                  disabled={runDue.isPending}
                >
                  {runDue.isPending ? 'جارٍ الإصدار' : `إصدار المستحق (${due.length})`}
                </button>
              )}
              <Link to="/finance/recurring/new" className="btn btn-primary">
                اشتراك جديد
              </Link>
            </>
          )
        }
      />

      <div className="stats">
        <Stat label="فعّالة" value={rows.filter((r) => r.status === 'ACTIVE').length} />
        <Stat label="مستحقة الآن" value={due.length} />
        {/*
          الإيراد المتكرر شهريًا: القيمة الحقيقية لهذه الشاشة.
          الاشتراك الربع سنوي يُقسَم على ثلاثة حتى تُقارن الأرقام على أساس واحد.
        */}
        <Stat
          label="إيراد متكرر شهريًا"
          value={<span className="num">{money(monthly)}</span>}
        />
      </div>

      {runDue.isSuccess && (
        <div className="alert alert-info mb-4">
          {runDue.data.issued > 0
            ? `أُصدرت ${runDue.data.issued} فاتورة. تجدها في الفواتير كمسودات ما لم يكن الاشتراك مضبوطًا على الإرسال التلقائي.`
            : 'لا فواتير مستحقة الآن.'}
          {runDue.data.failed > 0 && ` وتعذّر إصدار ${runDue.data.failed}.`}
        </div>
      )}

      {runDue.isError && (
        <div className="alert alert-danger mb-4">
          {runDue.error instanceof Error ? runDue.error.message : 'تعذّر الإصدار.'}
        </div>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالعنوان أو العميل"
        />

        <select
          className="select"
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {RECURRING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {RECURRING_STATUS_MAP[s].label}
            </option>
          ))}
        </select>

        {/* المدى على «الإصدار التالي»: ما سيُصدَر من فواتير بين اليومين. */}
        <DateRangeFilter
          from={state.from}
          to={state.to}
          onChange={(f, t) => patch({ from: f, to: t })}
        />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={RECURRING_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={RECURRING_FILTERS} filters={filters} onChange={setFilters} />

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <FilterScope
        defs={RECURRING_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/recurring/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الاشتراك
                  <HeaderFilter colKey="title" />
                </th>
                <th>
                  العميل
                  <HeaderFilter colKey="account" />
                </th>
                <th>
                  الدورة
                  <HeaderFilter colKey="cycle" />
                </th>
                <th>قيمة الدورة</th>
                <th>
                  الإصدار التالي
                  <HeaderFilter colKey="nextRunAt" />
                </th>
                <th>صدرت</th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
              </tr>
            </thead>

            {list.isLoading && <TableSkeleton rows={5} cols={7} />}

            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/finance/recurring/${r.id}`} className="cell-strong link">
                      {r.title}
                    </Link>
                    {r.autoIssue && <div className="tiny mute">إرسال تلقائي</div>}
                  </td>
                  <td className="cell-mute">{r.account.name}</td>
                  <td className="cell-mute">{RECURRENCE_CYCLE_MAP[r.cycle].label}</td>
                  <td className="num">{money(r.periodTotal)}</td>
                  <td
                    className={`num ${
                      r.status === 'ACTIVE' && r.nextRunAt <= today ? 'neg' : 'cell-mute'
                    }`}
                  >
                    {r.status === 'ENDED' ? '—' : date(r.nextRunAt)}
                  </td>
                  <td className="num cell-mute">{r.issuedCount}</td>
                  <td>
                    <Badge kind={RECURRING_STATUS_MAP[r.status].kind}>
                      {RECURRING_STATUS_MAP[r.status].label}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا اشتراكات دورية"
            text="أنشئ اشتراكًا للأتعاب الشهرية، فتصدر فواتيره وحدها في موعدها كل شهر."
          />
        )}

        {list.data && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      <p className="tiny mute mt-3">
        يفحص النظام الاشتراكات كل ساعة ويُصدر ما استحق. الفواتير تُنشأ مسودات ليراجعها أحد قبل
        إرسالها، ما لم تُفعّل "الإرسال التلقائي" في الاشتراك نفسه.
      </p>
    </div>
  );
}
